// Bảng nhân sự đầy đủ của từng bộ phận, sắp xếp được, tô màu tỷ lệ theo mức so với trung bình đội.
import { useMemo, useState } from 'react';
import { Avatar, Input, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { DEPT, RULES, fmtFull, fmtMoney, fmtRate, ratio, fmtD, type Dept, type Range, type Snapshot, type Totals } from './insights';
import { Panel } from './ui';

interface Row {
  key: number;
  name: string;
  avatar: string | null;
  a: number; // khối lượng đầu vào
  b: number; // kết quả
  c?: number;
  rate: number | null;
  rate2?: number | null;
  revenue?: number;
  aov?: number;
}

const num = (v?: number) => Math.round(v || 0).toLocaleString('vi-VN');

const RateCell = ({ v, avg }: { v: number | null; avg: number | null }) => {
  if (v === null) return <span className="text-slate-300">—</span>;
  const tone = avg && v < avg * RULES.lagFactor ? 'text-red-500 font-semibold' : avg && v >= avg * 1.2 ? 'text-emerald-600 font-semibold' : 'text-slate-700';
  return <span className={tone}>{fmtRate(v)}</span>;
};

export default function StaffTable({ cur, c, dept, className = '', range }: { cur: Snapshot; c: Totals; dept: Dept; className?: string; range?: Range }) {
  // Trực page: bấm tên để mở danh sách khách do nhân viên đó phụ trách trong kỳ
  const staffUrl = (id: number) => (dept === 'page' && range ? `/customer?${new URLSearchParams({ pstaff: String(id), from: fmtD(range.start), to: fmtD(range.end) }).toString()}` : null);
  const [q, setQ] = useState('');

  const rows: Row[] = useMemo(() => {
    if (dept === 'page') return (cur.page?.staff_customer_stats || []).map(s => ({
      key: s.staff_id, name: s.staff_name, avatar: s.staff_avatar, a: s.channel_customer_count, b: s.customer_count, rate: ratio(s.customer_count, s.channel_customer_count),
    }));
    if (dept === 'tele') return (cur.tele?.rankings || []).map(s => ({
      key: s.staff_id, name: s.name, avatar: s.avatar, a: s.booked, b: s.showup, c: s.closed, rate: Number(s.showup_rate), rate2: Number(s.close_rate),
    }));
    return (cur.sale?.rankings || []).map(s => ({
      key: s.staff_id, name: s.name, avatar: s.avatar, a: s.showup, b: s.closed, rate: Number(s.rate), revenue: s.revenue, aov: s.aov,
    }));
  }, [cur, dept]);

  const shown = useMemo(() => {
    const k = q.trim().toLowerCase();
    return k ? rows.filter(r => r.name.toLowerCase().includes(k)) : rows;
  }, [rows, q]);

  const nameCol: ColumnsType<Row>[number] = {
    title: 'Nhân sự', dataIndex: 'name', fixed: 'left', width: 220,
    render: (v, r) => (
      <span className="flex items-center gap-2">
        <Avatar size={26} src={r.avatar || undefined} style={{ background: DEPT[dept].color, flexShrink: 0 }}>{String(v).trim().split(/\s+/).pop()?.[0]}</Avatar>
        {staffUrl(r.key) ? <Link to={staffUrl(r.key)!} className="truncate font-medium text-slate-700 hover:text-[#357f96] hover:underline">{v}</Link> : <span className="truncate font-medium text-slate-700">{v}</span>}
      </span>
    ),
  };
  const numCol = (title: string, key: keyof Row, def?: boolean): ColumnsType<Row>[number] => ({
    title, dataIndex: key, width: 120, align: 'right', render: v => num(v),
    sorter: (x, y) => Number(x[key] || 0) - Number(y[key] || 0), defaultSortOrder: def ? 'descend' : undefined,
  });
  const rateCol = (title: string, key: 'rate' | 'rate2', avg: number | null): ColumnsType<Row>[number] => ({
    title, dataIndex: key, width: 130, align: 'right', render: v => <RateCell v={v ?? null} avg={avg} />,
    sorter: (x, y) => Number(x[key] ?? -1) - Number(y[key] ?? -1),
  });

  const columns: ColumnsType<Row> = dept === 'page'
    ? [nameCol, numCol('Khách nhắn tin', 'a'), numCol('Khách hàng mới', 'b', true), rateCol('Tỷ lệ lấy thông tin', 'rate', c.leadRate)]
    : dept === 'tele'
      ? [nameCol, numCol('Lịch đã đặt', 'a', true), numCol('Khách đến', 'b'), numCol('Khách chốt', 'c'), rateCol('Tỷ lệ đến', 'rate', c.showupRate), rateCol('Tỷ lệ chốt', 'rate2', c.teleCloseRate)]
      : [nameCol, numCol('Khách tiếp', 'a'), numCol('Khách chốt', 'b'), rateCol('Tỷ lệ chốt', 'rate', c.closeRate),
        { title: 'Doanh thu', dataIndex: 'revenue', width: 140, align: 'right', defaultSortOrder: 'descend', sorter: (x, y) => (x.revenue || 0) - (y.revenue || 0), render: v => <span className="font-semibold text-slate-800" title={fmtFull(v || 0)}>{fmtMoney(v || 0)}</span> },
        { title: 'Đơn trung bình', dataIndex: 'aov', width: 140, align: 'right', sorter: (x, y) => (x.aov || 0) - (y.aov || 0), render: v => <span title={fmtFull(v || 0)}>{fmtMoney(v || 0)}</span> }];

  return (
    <Panel
      title="Hiệu suất nhân sự" className={className}
      info={`Tỷ lệ màu đỏ: thấp hơn trung bình đội từ ${Math.round((1 - RULES.lagFactor) * 100)}% trở lên. Màu xanh: cao hơn trung bình đội từ 20%.`}
      extra={<Input allowClear size="small" prefix={<Search size={13} className="text-slate-400" />} placeholder={`Tìm trong ${shown.length} nhân sự`} value={q} onChange={e => setQ(e.target.value)} className="w-44 sm:w-52" />}
    >
      <Table<Row>
        size="middle" rowKey="key" columns={columns} dataSource={shown} scroll={{ x: 760 }}
        pagination={shown.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
        locale={{ emptyText: 'Chưa có dữ liệu nhân sự trong kỳ' }}
      />
    </Panel>
  );
}
